import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ip_user") || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ip_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      localStorage.setItem("ip_user", JSON.stringify(r.data));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    // Every successful primary auth now returns an mfa_required flag — the
    // caller must complete the email OTP challenge before we set the user.
    if (r.data.mfa_required) {
      return {
        mfaRequired: true,
        mfaTicket: r.data.mfa_ticket,
        email: r.data.email,
      };
    }
    // If backend signals must_change_password we don't set the user yet — the
    // caller (SignIn) will route to the Set Password screen using the returned
    // short-lived token.
    if (r.data.must_change_password) {
      localStorage.setItem("ip_token", r.data.token);
      return { mustChangePassword: true, user: r.data.user };
    }
    localStorage.setItem("ip_token", r.data.token);
    localStorage.setItem("ip_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  const verifyMfa = async (mfaTicket, code) => {
    const r = await api.post(
      "/auth/mfa/verify",
      { code },
      { headers: { Authorization: `Bearer ${mfaTicket}` } },
    );
    localStorage.setItem("ip_token", r.data.token);
    localStorage.setItem("ip_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  const resendMfa = async (mfaTicket) => {
    await api.post(
      "/auth/mfa/resend",
      {},
      { headers: { Authorization: `Bearer ${mfaTicket}` } },
    );
  };

  const completePasswordChange = (token, user) => {
    localStorage.setItem("ip_token", token);
    localStorage.setItem("ip_user", JSON.stringify(user));
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem("ip_token");
    localStorage.removeItem("ip_user");
    setUser(null);
    window.location.href = "/signin";
  };

  const isAdmin = user?.role === "super_admin";
  const isTeam = user?.role === "investment_team" || user?.role === "super_admin";
  const isInvestor = user?.role === "investor";
  const isClient = user?.role === "client";

  return (
    <AuthCtx.Provider value={{ user, login, logout, loading, isAdmin, isTeam, isInvestor, isClient, completePasswordChange, verifyMfa, resendMfa }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
